import { debounce } from 'lodash';
import { useCallback, useMemo } from 'react';

import { mapValueObjectsToOptions } from '~/components/Shared/EcSelect/EcSelect.utils';
import { api } from '~/redux/api/createApi';

const MIN_INPUT_LENGTH = 3;
const PARTIES_FIELDS = ['Counterparties', 'Internal Parties'];

interface UseAsyncFieldValuesOptions {
  fieldId: number | string;
  className?: string;
  allowNewOptions?: boolean;
  currentValue?: any;
  onChange?: (fieldId: number, newValue: any[]) => void;
}

export const useAsyncFieldValues = ({
  fieldId,
  className,
  allowNewOptions,
  currentValue,
  onChange,
}: UseAsyncFieldValuesOptions) => {
  const [getFieldValues, result] = api.endpoints.getFieldValues.useLazyQuery();
  const debouncedGetFieldValues = useCallback(debounce(getFieldValues, 300), [
    getFieldValues,
  ]);

  const { data, error, isFetching } = result;

  const isPartiesField = className ? PARTIES_FIELDS.includes(className) : false;

  const asyncOptions = useMemo(
    () => ({
      data: mapValueObjectsToOptions(data?.values || []),
      error,
      isFetching,
      selectAllCount: data?.selectAllCount,
    }),
    [data, error, isFetching],
  );

  const fetchOptions = useCallback(
    (substringQuery = '', immediate = false) => {
      const queryFn = immediate ? getFieldValues : debouncedGetFieldValues;
      queryFn(
        {
          fieldId: String(fieldId),
          searchSuggestions: false,
          substringQuery,
        },
        true,
      );
    },
    [debouncedGetFieldValues, getFieldValues, fieldId],
  );

  const handleSearch = useCallback(
    (searchQuery = '') => {
      if (
        isPartiesField &&
        searchQuery.length > 0 &&
        searchQuery.length < MIN_INPUT_LENGTH
      ) {
        return;
      }
      const immediate = searchQuery === '';
      fetchOptions(searchQuery, immediate);
    },
    [isPartiesField, fetchOptions],
  );

  const handleChange = useCallback(
    (newValues: any) => {
      if (!onChange) return;

      const newItems = Array.isArray(newValues)
        ? newValues.map((val: any) => {
            const option = asyncOptions.data.find(
              (opt: { value: any; label: string }) => opt.value === val,
            );
            return {
              value: val,
              label: option?.label || String(val),
            };
          })
        : [];
      onChange(Number(fieldId), newItems);
    },
    [onChange, fieldId, asyncOptions.data],
  );

  const handleAddOption = useCallback(
    (newOption: any) => {
      if (!onChange || !allowNewOptions) return undefined;

      const newItem = {
        value: newOption.value,
        label: newOption.label,
      };
      const currentItems = Array.isArray(currentValue) ? currentValue : [];
      onChange(Number(fieldId), [...currentItems, newItem]);
    },
    [onChange, allowNewOptions, currentValue, fieldId],
  );

  const shouldLoadOnMount = !isPartiesField;

  return {
    asyncOptions,
    fetchOptions,
    handleSearch,
    handleChange,
    handleAddOption: allowNewOptions ? handleAddOption : undefined,
    shouldLoadOnMount,
    isPartiesField,
  };
};
